import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps
} from "components/common/FormDialog/types";
import { GaugeChartMetric } from "components/common/GaugeChartMetric";
import { Head } from "components/common/Head";
import { Loader } from "components/common/Loader";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { userSelector } from "modules/auth/selectors";
import { databasesActions } from "modules/databases/slices";
import { dbaasQuotasSelector } from "modules/databases/selectors";
import { enterprisesActions } from "modules/enterprises/slices";
import { organizationSelector } from "modules/enterprises/selectors";
import { ROLES } from "modules/enterprises/types";
import { instancesActions } from "modules/instances/slices";
import { instanceLimitsSelector } from "modules/instances/selectors";
import { networksActions } from "modules/networks/slices";
import { networkLimitsSelector } from "modules/networks/selectors";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import {
  isProjectDeletingSelector,
  isProjectUpdatingSelector,
  projectSelector
} from "modules/projects/selectors";
import { storageActions as volumesActions } from "modules/storage/slices";
import { volumeLimitsSelector } from "modules/storage/selectors";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { getParentPath } from "utils/getParentPath";
import { validateName } from "utils/validateName";
import { string } from "yup";
import { appConfig } from "../../appConfig";
import {
  ERROR_MESSAGES,
  PROJECT_NAME_LENGTH,
  REGEX,
  ROUTES
} from "../../constants";
import * as s from "./styles";
import { DIALOG_TYPES } from "./types";

const POLL_ID_PREFIX = "PROJECT";

const POLL_IDS = {
  dbaasQuotas: "DBAAS_QUOTAS",
  instanceLimits: "INSTANCE_LIMITS",
  volumeLimits: "VOLUME_LIMITS",
  networkLimits: "NETWORK_LIMITS",
  project: "PROJECT"
};

export const Project: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    serviceName: string;
    organizationId: string;
    regionId: string;
    projectId: string;
  }>();
  const history = useNavigate();
  const user = useSelector(userSelector);
  const organization = useSelector(organizationSelector);
  const project = useSelector(projectSelector);
  const dbaasQuotas = useSelector(dbaasQuotasSelector);
  const instanceLimits = useSelector(instanceLimitsSelector);
  const volumeLimits = useSelector(volumeLimitsSelector);
  const networkLimits = useSelector(networkLimitsSelector);
  const isProjectUpdating = useSelector(isProjectUpdatingSelector);
  const isProjectDeleting = useSelector(isProjectDeletingSelector);
  const isOperationInProgress = isProjectUpdating || isProjectDeleting;
  const previousIsOperationInProgress = usePrevious(isOperationInProgress);

  const showBackups =
    appConfig.isBackupsEnabled ||
    user?.attributes?.["canaryFeatures"]?.includes("backups");
  const showDatabases = appConfig.isDbaasEnabled;

  const hiddenIds: string[] = [];
  if (!showBackups) hiddenIds.push("Backups");
  if (!showDatabases) hiddenIds.push("Databases");

  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.EDIT, isOpened: false });

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
  }, [dialog]);

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Projects",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: project?.name || "",
      url: generatePath(ROUTES.PROJECT, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    }
  ];

  const handleEditProjectButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.EDIT,
      isOpened: true
    });
  }, []);

  const handleDeleteProjectButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.DELETE,
      isOpened: true
    });
  }, []);

  useMount(() => {
    dispatch(
      projectsActions.getProjectRequest({
        regionId: matchParams.regionId!,
        id: matchParams.projectId!
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.instanceLimits}`,
        actionCreator: instancesActions.getInstanceLimitsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.volumeLimits}`,
        actionCreator: volumesActions.getVolumeLimitsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.networkLimits}`,
        actionCreator: networksActions.getNetworkLimitsRequest,
        params: {
          regionId: matchParams.regionId!,
          projectId: matchParams.projectId!
        }
      })
    );
    if (showDatabases) {
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.dbaasQuotas}`,
          actionCreator: databasesActions.getDbaasQuotasRequest,
          params: {
            serviceName: matchParams.serviceName!,
            orgId: matchParams.organizationId!,
            projId: matchParams.projectId!
          }
        })
      );
    }
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
    dispatch(instancesActions.clear());
    dispatch(volumesActions.clear());
    dispatch(networksActions.clear());
    if (appConfig.isDbaasEnabled) {
      dispatch(databasesActions.clear());
    }
  });

  useEffect(() => {
    if (previousIsOperationInProgress && !isOperationInProgress) {
      if (!project) {
        history(getParentPath(location.pathname, 4));
      } else {
        dispatch(
          projectsActions.getProjectRequest({
            regionId: matchParams.regionId!,
            id: matchParams.projectId!
          })
        );
      }
    }
  }, [
    project,
    history,
    previousIsOperationInProgress,
    isOperationInProgress,
    dispatch,
    matchParams.regionId,
    matchParams.projectId
  ]);

  const handleConfirmEditProject = useCallback(
    (data: { name: string }) => {
      dispatch(
        projectsActions.updateProjectRequest({
          organizationId: matchParams.organizationId!,
          regionId: matchParams.regionId!,
          id: matchParams.projectId!,
          data
        })
      );
      handleCloseDialog();
    },
    [
      dispatch,
      handleCloseDialog,
      matchParams.organizationId,
      matchParams.regionId,
      matchParams.projectId
    ]
  );

  const handleConfirmDeleteProject = useCallback(() => {
    dispatch(
      projectsActions.deleteProjectRequest({
        organizationId: matchParams.organizationId!,
        regionId: matchParams.regionId!,
        id: matchParams.projectId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    handleCloseDialog,
    matchParams.organizationId,
    matchParams.regionId,
    matchParams.projectId
  ]);

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.EDIT]: {
      onConfirm: handleConfirmEditProject,
      title: "Edit project",
      confirmButtonLabel: "Save",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          defaultValue: project?.name || "",
          rules: string()
            .required()
            .test({
              name: "validateName",
              test: validateName(PROJECT_NAME_LENGTH)
            })
            .matches(REGEX.PROJECT_NAME, ERROR_MESSAGES.PROJECT_NAME)
        }
      ]
    },
    [DIALOG_TYPES.DELETE]: {
      onConfirm: handleConfirmDeleteProject,
      title: `Are you sure you want to delete "${
        project?.name ?? "selected"
      }" project?`,
      fields: [
        {
          name: "confirmationName",
          type: FIELD_TYPES.TEXT,
          label: "Type the project name to confirm deletion",
          rules: string()
            .required()
            .test({
              name: "validateConfirmationName",
              message: "Project name does not match",
              test: function (value) {
                return value === project?.name;
              }
            })
        },
        {
          name: "deletionInfo",
          type: FIELD_TYPES.LABEL,
          label: `\n\nℹ️ Deleting a Project will permanently erase all related resources, including virtual machines, Kubernetes clusters, volumes, and other components, with no option for recovery.`
        }
      ],
      confirmButtonLabel: "Delete"
    }
  };

  const title = project?.name;

  const renderMetric = (
    title: string,
    usedValue: number,
    maxValue: number,
    unit: string = ""
  ) => (
    <GaugeChartMetric
      title={title}
      usedValue={usedValue}
      maxValue={maxValue}
      unit={unit}
    />
  );

  return (
    <>
      <Head title={title} />
      {project ? (
        <>
          {organization && <Breadcrumbs breadcrumbs={breadcrumbs} />}
          <s.SummaryContainer>
            <s.Title
              variant={"h4"}
              component={"h2"}
              data-testid={"project-title"}
            >
              {title}
            </s.Title>

            <s.ActionsContainer>
              <Tooltip title={"Edit"} arrow>
                <span>
                  <IconButton
                    data-testid={"edit-project-button"}
                    onClick={handleEditProjectButtonClick}
                    color={"inherit"}
                    disabled={
                      !organization ||
                      ![ROLES.OWNER, ROLES.ADMIN].includes(organization.role)
                    }
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={"Delete"} arrow>
                <span>
                  <IconButton
                    data-testid={"delete-project-button"}
                    onClick={handleDeleteProjectButtonClick}
                    color={"inherit"}
                    disabled={
                      !organization ||
                      ![ROLES.OWNER, ROLES.ADMIN].includes(organization.role)
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </s.ActionsContainer>
          </s.SummaryContainer>
          {instanceLimits && volumeLimits && networkLimits ? (
            <>
              <s.SummaryRow>
                <s.DetailsTitle>ID: </s.DetailsTitle>
                <s.DetailsInfoColored>{project.id}</s.DetailsInfoColored>
              </s.SummaryRow>
              <s.SummaryRow>
                <s.DetailsTitle>Region: </s.DetailsTitle>
                <s.Tag label={instanceLimits?.region} />
              </s.SummaryRow>

              <s.MetricsContainer variant={"outlined"}>
                <s.MetricRowContainer>
                  {renderMetric(
                    "Virtual machines",
                    instanceLimits.absolute.totalInstancesUsed,
                    instanceLimits.absolute.maxTotalInstances
                  )}
                  {renderMetric(
                    "VM total vCPUs",
                    instanceLimits.absolute.totalCoresUsed,
                    instanceLimits.absolute.maxTotalCores
                  )}
                  {renderMetric(
                    "VM total memory",
                    instanceLimits.absolute.totalRAMUsed / 1024,
                    instanceLimits.absolute.maxTotalRAMSize / 1024,
                    "GiB"
                  )}
                </s.MetricRowContainer>
                <s.MetricRowContainer>
                  {renderMetric(
                    "Volumes",
                    volumeLimits.absolute.totalVolumesUsed,
                    volumeLimits.absolute.maxTotalVolumes
                  )}
                  {renderMetric(
                    "Volume snapshots",
                    volumeLimits.absolute.totalSnapshotsUsed,
                    volumeLimits.absolute.maxTotalSnapshots
                  )}
                  {renderMetric(
                    "Volume storage",
                    volumeLimits.absolute.totalGigabytesUsed,
                    volumeLimits.absolute.maxTotalVolumeGigabytes,
                    "GiB"
                  )}
                </s.MetricRowContainer>
                <s.MetricRowContainer>
                  {renderMetric(
                    "Networks",
                    networkLimits.network_used,
                    networkLimits.network_limits
                  )}
                  {renderMetric(
                    "Firewalls",
                    networkLimits.security_groups_used,
                    networkLimits.security_groups_limits
                  )}
                  {renderMetric(
                    "Firewall rules",
                    networkLimits.security_groups_rules_used,
                    networkLimits.security_groups_rules_limits
                  )}
                </s.MetricRowContainer>
                {appConfig.isDbaasEnabled && dbaasQuotas ? (
                  <>
                    <s.MetricRowContainer>
                      {renderMetric(
                        "Databases",
                        dbaasQuotas.current_value_of_instance,
                        dbaasQuotas.max_value_of_instance
                      )}
                      {renderMetric(
                        "DB total vCPUs",
                        dbaasQuotas.current_value_of_cpu / 1000,
                        dbaasQuotas.max_value_of_cpu / 1000
                      )}
                      {renderMetric(
                        "DB total memory",
                        dbaasQuotas.current_value_of_memory / 1024,
                        dbaasQuotas.max_value_of_memory / 1024,
                        "GiB"
                      )}
                    </s.MetricRowContainer>
                    <s.MetricRowContainer>
                      {renderMetric(
                        "DB total storage",
                        dbaasQuotas.current_value_of_disk / 1024,
                        dbaasQuotas.max_value_of_disk / 1024,
                        "GiB"
                      )}
                    </s.MetricRowContainer>
                  </>
                ) : null}
              </s.MetricsContainer>
            </>
          ) : (
            <Loader text={"Loading data..."} />
          )}
        </>
      ) : (
        <Loader text={"Loading data..."} />
      )}
      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        fields={dialogProps[dialog.type].fields}
        onConfirm={dialogProps[dialog.type].onConfirm}
        title={dialogProps[dialog.type].title}
        confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
      />
    </>
  );
};
